/**
 * AppSyncSubscriptionLink
 *
 * Implements the AWS AppSync real-time WebSocket subscription protocol as an
 * Apollo Link. This is required because the standard `graphql-ws` / `subscriptions-transport-ws`
 * libraries do NOT implement the AppSync-specific handshake:
 *
 *  1. Connect to `wss://{realtime-host}/graphql/realtime?header={base64(authObj)}&payload={base64({})}`
 *     using the `graphql-ws` WebSocket sub-protocol.
 *  2. Send `{"type":"connection_init"}`.
 *  3. Receive `{"type":"connection_ack","payload":{"connectionTimeoutMs":N}}`.
 *  4. Send `{"id":"...","type":"start","payload":{"data":"{\"query\":\"...\",\"variables\":{...}}","extensions":{"authorization":{...}}}}`.
 *  5. Receive `{"id":"...","type":"data","payload":{"data":{...}}}` for each event.
 *  6. Optionally send `{"id":"...","type":"stop"}` to unsubscribe.
 *
 * Reference: the demo HTML in CR-278082531 (`lib/stacks/demoStreaming/demo/index.html`).
 *
 * Auth: the caller supplies a `getAuthHeader()` function that returns the
 * current Midway bearer token. The Authorization value is sent verbatim (the
 * Midway authorizer expects `Bearer <token>`).
 */

import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, NextLink, Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode, print } from 'graphql';

export interface AppSyncSubscriptionLinkOptions {
  /**
   * The HTTPS GraphQL endpoint (e.g. `https://api.iad.gamma.antenna-dp.groundstation.aws.dev/graphql`).
   * The link derives the realtime WebSocket URL from this.
   */
  url: string;

  /**
   * Returns the current Authorization header value (e.g. `"Bearer <token>"`).
   * Called once when each subscription starts so the token is fresh.
   */
  getAuthHeader: () => string;
}

/** Keep-alive watchdog: reconnect if the server goes quiet longer than this. */
const DEFAULT_KA_TIMEOUT_MS = 300_000;

/** Initial delay before retrying after an unexpected close. */
const INITIAL_RECONNECT_DELAY_MS = 3_000;
/** Maximum delay between reconnection attempts (backoff cap). */
const MAX_RECONNECT_DELAY_MS = 60_000;

export class AppSyncSubscriptionLink extends ApolloLink {
  private readonly options: AppSyncSubscriptionLinkOptions;

  constructor(options: AppSyncSubscriptionLinkOptions) {
    super();
    this.options = options;
  }

  public override request(operation: Operation, forward: NextLink): ReturnType<ApolloLink['request']> {
    const definition = getMainDefinition(operation.query);
    const isSubscription =
      definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;

    // Non-subscription operations pass through to the next link (HTTP).
    if (!isSubscription) {
      return forward(operation);
    }

    return this.createSubscriptionObservable(operation);
  }

  private createSubscriptionObservable(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>(observer => {
      let ws: WebSocket | null = null;
      let kaTimer: ReturnType<typeof setTimeout> | null = null;
      let kaTimeoutMs = DEFAULT_KA_TIMEOUT_MS;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let subId: string | null = null;
      let stopped = false;
      let reconnectAttempts = 0;

      const resetKaWatchdog = () => {
        if (kaTimer !== null) clearTimeout(kaTimer);
        kaTimer = setTimeout(() => {
          if (!stopped) {
            console.warn('[AppSyncSubscriptionLink] keep-alive timeout; reconnecting');
            ws?.close();
          }
        }, kaTimeoutMs);
      };

      const connect = () => {
        if (stopped) return;

        const { url, getAuthHeader } = this.options;

        // Derive the realtime endpoint.
        // Custom domain (`api.iad.gamma.antenna-dp.groundstation.aws.dev`):
        // the realtime path is just /graphql/realtime on the same host.
        // Raw AppSync URL (`abc.appsync-api.us-east-1.amazonaws.com/graphql`):
        // replace `appsync-api` → `appsync-realtime-api`.
        let graphqlHost: string;
        try {
          graphqlHost = new URL(url).host;
        } catch {
          observer.error(new Error(`[AppSyncSubscriptionLink] invalid URL: ${url}`));
          return;
        }

        const realtimeHost = graphqlHost.includes('appsync-api')
          ? graphqlHost.replace('appsync-api', 'appsync-realtime-api')
          : graphqlHost;

        const authHeader = getAuthHeader();
        // Auth object for AppSync: host + Authorization (verbatim, including "Bearer " prefix).
        const authObj: Record<string, string> = {
          host: graphqlHost,
          Authorization: authHeader,
        };
        // btoa is safe here: authObj contains only ASCII (hostname + JWT bearer token).
        const header = btoa(JSON.stringify(authObj));
        const payload = btoa('{}');
        const wsUrl = `wss://${realtimeHost}/graphql/realtime?header=${encodeURIComponent(header)}&payload=${encodeURIComponent(payload)}`;

        ws = new WebSocket(wsUrl, 'graphql-ws');

        ws.onopen = () => {
          // ws is guaranteed non-null — we are inside the onopen callback that
          // WebSocket itself fires. Capture in a local const to satisfy the linter.
          const socket = ws;
          if (socket === null) return;
          socket.send(JSON.stringify({ type: 'connection_init' }));
        };

        ws.onmessage = event => {
          let msg: { type: string; id?: string; payload?: unknown };
          try {
            msg = JSON.parse(event.data as string) as typeof msg;
          } catch {
            return;
          }

          switch (msg.type) {
            case 'connection_ack': {
              const p = msg.payload as { connectionTimeoutMs?: number } | undefined;
              if (p?.connectionTimeoutMs != null) kaTimeoutMs = p.connectionTimeoutMs;
              reconnectAttempts = 0; // successful connection resets backoff
              resetKaWatchdog();
              sendSubscription(authObj);
              break;
            }
            case 'ka':
              resetKaWatchdog();
              break;
            case 'start_ack':
              // Subscription registered — nothing to do.
              break;
            case 'data': {
              resetKaWatchdog();
              if (!stopped) {
                const data = (msg.payload as { data?: Record<string, unknown> } | undefined)?.data;
                if (data !== undefined) {
                  observer.next({ data } as FetchResult);
                }
              }
              break;
            }
            case 'error':
              if (!stopped) {
                observer.error(
                  new Error(`[AppSyncSubscriptionLink] subscription error: ${JSON.stringify(msg.payload)}`),
                );
              }
              break;
            case 'complete':
              if (!stopped) observer.complete();
              break;
            default:
              break;
          }
        };

        ws.onerror = err => {
          console.error('[AppSyncSubscriptionLink] WebSocket error', err);
        };

        ws.onclose = closeEvent => {
          if (kaTimer !== null) {
            clearTimeout(kaTimer);
            kaTimer = null;
          }
          if (!stopped) {
            // Exponential backoff with jitter: delay doubles each attempt, capped at MAX.
            const baseDelay = Math.min(INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY_MS);
            const jitter = Math.random() * baseDelay * 0.3;
            const delay = Math.round(baseDelay + jitter);
            reconnectAttempts++;
            console.warn(
              `[AppSyncSubscriptionLink] WebSocket closed (${closeEvent.code}), reconnecting in ${delay}ms (attempt ${reconnectAttempts})`,
            );
            reconnectTimer = setTimeout(connect, delay);
          }
        };
      };

      const sendSubscription = (authObj: Record<string, string>) => {
        if (ws === null || ws.readyState !== WebSocket.OPEN) return;
        subId = `sub-${Date.now()}`;
        const queryStr = print(operation.query);
        ws.send(
          JSON.stringify({
            id: subId,
            type: 'start',
            payload: {
              // AppSync expects the query+variables as a JSON *string* inside `data`.
              data: JSON.stringify({
                query: queryStr,
                variables: operation.variables,
              }),
              extensions: {
                authorization: authObj,
              },
            },
          }),
        );
      };

      // Start the connection.
      connect();

      // Cleanup: called when the subscriber unsubscribes or the component unmounts.
      return () => {
        stopped = true;
        if (kaTimer !== null) {
          clearTimeout(kaTimer);
          kaTimer = null;
        }
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        if (ws !== null) {
          try {
            if (ws.readyState === WebSocket.OPEN && subId !== null) {
              ws.send(JSON.stringify({ id: subId, type: 'stop' }));
            }
            // Suppress the reconnect handler — we're cleaning up intentionally.
            ws.onclose = null;
            ws.close();
          } catch {
            // Ignore errors during cleanup.
          }
          ws = null;
        }
      };
    });
  }
}
